import { useEffect, useRef, useState } from "react";
import { withText } from "./png";
import type { StoryView } from "../shared/story";
import { goToTurn } from "./nav";
import { DEFAULT_SKIN, SKINS, type Skin } from "./skins";

/**
 * The cloth view: the whole story as one woven textile. Warp (vertical) threads are the story's threads, each in its
 * own colour, starting at the row where it entered and tied off where it closed. Weft (horizontal) rows are turns:
 * heavier and brighter with tension, passing over the warp except where the turn touched a thread (there the thread
 * rises to the surface). A cut row frays short; a sudden event leaves a knot; a new chapter, a band. Tap a row to go
 * to that turn. It saves as a 4:5 image with no words from the story in it.
 */

/** Thread colours: eight that stay apart (in Phosphor, eight ambers apart in lightness and warmth). */
/** Nitrate: the tints silent films were dyed in (amber night, blue dusk, rose dawn…), all light on the black. */
const FILM_TINTS = ["#d9a441", "#7fa9d4", "#d48a9a", "#86b892", "#b1a1d8", "#c99a70", "#69c0bb", "#ddd08f"];
/** Cyanotype: what light leaves on the blue: creams and pale blues, apart in lightness and warmth. */
const PRINT_TINTS = ["#f4ead2", "#9fd2f0", "#e8c48f", "#cfe3f5", "#8fd1c3", "#b5b8ef", "#f2d9e3", "#6fb3e6"];
const INK_HUES = [212, 352, 140, 280, 28, 190, 320, 80];
const AMBERS = [[36, 100, 72], [24, 90, 48], [44, 95, 84], [30, 60, 36], [48, 100, 60], [18, 85, 62], [40, 70, 92], [28, 100, 28]] as const;
const PALETTES: Record<Skin, { ground: string; weft: [number, number, number]; band: string; knot: string; color: (i: number) => string }> = {
  nitrate: { ground: "#0c0c0b", weft: [222, 217, 204], band: "#e6e1d4", knot: "#ffffff", color: (i) => FILM_TINTS[i % 8]! },
  cyanotype: { ground: "#143e6b", weft: [226, 232, 240], band: "#f4f0e4", knot: "#ffffff", color: (i) => PRINT_TINTS[i % 8]! },
  edition: { ground: "#ebe6da", weft: [40, 38, 32], band: "#a52c18", knot: "#a52c18", color: (i) => `hsl(${INK_HUES[i % 8]} 48% 33%)` },
  manuscript: { ground: "#efe6d2", weft: [60, 44, 30], band: "#9a2f1d", knot: "#9a2f1d", color: (i) => `hsl(${INK_HUES[i % 8]} 42% 36%)` },
  phosphor: { ground: "#0c0904", weft: [240, 168, 58], band: "#ffd27a", knot: "#fff2c8", color: (i) => { const [h, sa, l] = AMBERS[i % 8]!; return `hsl(${h} ${sa}% ${l}%)`; } },
};
export const skinNow = (): Skin => (SKINS.some((k) => k.id === document.documentElement.dataset.skin) ? document.documentElement.dataset.skin as Skin : DEFAULT_SKIN);
const hash = (s: string) => { let h = 2166136261; for (let i = 0; i < s.length; i++) h = Math.imul(h ^ s.charCodeAt(i), 16777619); return h >>> 0; };

/**
 * The story's woven layout: which rows and lanes exist, used for drawing and for hit-testing a tap. Threads share
 * lanes: one that starts after another closed takes its lane, so the cloth is as wide as the most threads alive at
 * once, not as the story's whole history. Colours come from a fixed palette, never the same for two threads alive
 * together.
 */
interface Span { t: StoryView["threads"][number]; start: number; end: number; color: number }
function layout(view: StoryView) {
  const rows = view.turns.filter((t) => t.prose.trim() || t.input.trim());
  const first = rows[0]?.n ?? 1;
  const spans: Span[] = [...view.threads]
    .sort((a, b) => a.turn - b.turn || a.id.localeCompare(b.id))
    .map((t) => ({ t, start: t.turn, end: t.state === "closed" ? Math.max(t.turn, ...t.seen) : Infinity, color: 0 }));
  const lanes: Span[][] = [];
  for (const sp of spans) {
    const lane = lanes.find((l) => l.at(-1)!.end < sp.start);
    if (lane) lane.push(sp); else if (lanes.length < MAX_LANES) lanes.push([sp]); else continue;
    const busy = new Set(spans.filter((o) => o !== sp && o.start <= sp.end && sp.start <= o.end && lanes.some((l) => l.includes(o))).map((o) => o.color));
    let c = 0;
    while (busy.has(c) && c < PALETTE_SIZE - 1) c++;
    sp.color = c;
  }
  // Each lane is a few warp ends with ground ends between (so even a short story has the texture of cloth).
  const ends: { warp: number | null }[] = [];
  lanes.forEach((_, j) => { if (j) for (let k = 0; k < GAP; k++) ends.push({ warp: null }); for (let k = 0; k < ENDS; k++) ends.push({ warp: j }); });
  for (let k = 0; k < GAP; k++) { ends.unshift({ warp: null }); ends.push({ warp: null }); }
  /** The thread in lane j at turn n, if one is alive there. */
  const at = (j: number, n: number) => lanes[j]?.find((sp) => n >= sp.start && n <= sp.end) ?? null;
  return { rows, first, lanes, ends, at };
}
/** Each thread's colour on the cloth (the story map draws threads in the same colours). */
export function threadColors(view: StoryView, skin: Skin): Map<string, string> {
  const P = PALETTES[skin];
  return new Map(layout(view).lanes.flat().map((sp) => [sp.t.id, P.color(sp.color)]));
}
const MAX_LANES = 10;
const PALETTE_SIZE = 8;
const ENDS = 3, GAP = 2;
export const PAD = 0.07;

/** What is under a point of a W×H cloth: the turn's row, and the story thread whose warp is there (if any). */
export function hitTest(view: StoryView, W: number, H: number, x: number, y: number) {
  const { rows, ends, at } = layout(view);
  const pad = Math.round(W * PAD);
  const fx = (x - pad) / (W - pad * 2), fy = (y - pad) / (H - pad * 2);
  if (!rows.length || fy < 0 || fy >= 1 || fx < 0 || fx >= 1) return null;
  const row = rows[Math.floor(fy * rows.length)]!;
  const e = ends[Math.floor(fx * ends.length)];
  const sp = e && e.warp !== null ? at(e.warp, row.n) : null;
  const thread = sp ? sp.t : null;
  return { row, index: Math.floor(fy * rows.length), rows: rows.length, thread, touched: !!thread && thread.seen.includes(row.n) };
}

/** The rows (turns) of the cloth, in order: what the story rail maps its height onto. */
export const clothRows = (view: StoryView) => layout(view).rows.map((r) => r.n);

/** Where turn `n`'s row lies on a W×H cloth: its top and bottom in px (a turn with no row takes the next one's). */
export function rowSpan(view: StoryView, W: number, H: number, n: number): { y0: number; y1: number } | null {
  const rows = clothRows(view);
  if (!rows.length) return null;
  let i = rows.findIndex((r) => r >= n);
  if (i < 0) i = rows.length - 1;
  const pad = Math.round(W * PAD), ih = H - pad * 2;
  return { y0: pad + (i / rows.length) * ih, y1: pad + ((i + 1) / rows.length) * ih };
}

/** `picks`: weft picks per turn (default: enough for a small cloth to read as woven; the rail's loupe asks for more). */
export function drawCloth(g: CanvasRenderingContext2D, view: StoryView, W: number, H: number, skin: Skin = skinNow(), o: { picks?: number } = {}): void {
  const P = PALETTES[skin];
  const { rows, lanes, ends, at } = layout(view);
  const pad = Math.round(W * PAD);
  const iw = W - pad * 2, ih = H - pad * 2;
  g.fillStyle = P.ground; g.fillRect(0, 0, W, H);
  if (!rows.length) return;
  const rnd = (seed: string) => (hash(seed) % 1000) / 1000;
  // Each story thread is a few warp ends with ground ends between; each turn is a few weft picks, so even a short
  // story has the texture of cloth. Every crossing is drawn as a small shaded float, over or under.
  const picks = o.picks ?? Math.max(2, Math.min(5, Math.round(48 / rows.length)));
  const cw = iw / ends.length, ph = ih / (rows.length * picks);
  const [wr, wg, wb] = P.weft;
  const float = (x: number, y: number, w: number, h: number, color: string, vertical: boolean, lift: number) => {
    const r = Math.min(w, h) * 0.45;
    const grad = vertical ? g.createLinearGradient(x, y, x + w, y) : g.createLinearGradient(x, y, x, y + h);
    grad.addColorStop(0, `rgba(0,0,0,${0.35 * lift})`); grad.addColorStop(0.5, "rgba(255,255,255,0)"); grad.addColorStop(1, `rgba(0,0,0,${0.45 * lift})`);
    g.fillStyle = color;
    g.beginPath(); g.roundRect(x, y, w, h, r); g.fill();
    g.fillStyle = grad; g.beginPath(); g.roundRect(x, y, w, h, r); g.fill();
    // A soft highlight along the float's crown.
    g.fillStyle = `rgba(255,255,255,${0.10 * lift})`;
    if (vertical) g.fillRect(x + w * 0.42, y + h * 0.15, w * 0.16, h * 0.7); else g.fillRect(x + w * 0.1, y + h * 0.4, w * 0.8, h * 0.14);
  };
  rows.forEach((t, i) => {
    const tension = Math.max(1, Math.min(5, t.tension ?? 3));
    const kept = t.status === "cut" && t.unwoven ? Math.max(0.2, 1 - t.unwoven / (t.unwoven + Math.max(20, t.prose.split(/\s+/).length))) : 1;
    const x1 = pad + iw * kept;
    // Tension in the weft: quiet turns are thin and dim, tense ones dense and bright.
    const weft = `rgba(${wr},${wg},${wb},${0.16 + tension * 0.165})`;
    const band = view.chapterStarts.includes(t.n);
    for (let p = 0; p < picks; p++) {
      const r = i * picks + p;
      const y = pad + r * ph;
      ends.forEach((e, c) => {
        const x = pad + c * cw;
        if (x + cw > x1 + 0.5) return;
        const w = e.warp === null ? null : at(e.warp, t.n);
        const live = !!w;
        const touched = live && w.t.seen.includes(t.n);
        // Plain weave; a touched thread floats over most picks so its colour rises to the surface.
        const warpOver = live ? (touched ? (r + c) % 3 !== 0 : (r + c) % 2 === 0) : (r + c) % 2 === 0;
        const warpColor = live ? P.color(w.color) : `rgba(${wr},${wg},${wb},0.18)`;
        const pickColor = band && p === 0 ? P.band : weft;
        if (warpOver) float(x + cw * 0.08, y - ph * 0.08, cw * 0.84, ph * 1.16, warpColor, true, touched ? 1 : 0.8);
        else float(x - cw * 0.06, y + ph * (0.24 - tension * 0.035), cw * 1.12, ph * (0.52 + tension * 0.07), pickColor, false, 0.6 + tension * 0.08);
      });
    }
    // A cut row frays: loose fibres past where the player stopped reading.
    if (kept < 1) {
      g.strokeStyle = `rgba(${wr},${wg},${wb},.6)`; g.lineWidth = Math.max(0.7, ph * 0.18); g.lineCap = "round";
      for (let k = 0; k < picks * 3; k++) {
        const fy = pad + (i * picks + (k % picks) + 0.5) * ph;
        const len = ph * (2 + rnd(`${t.n}:l${k}`) * 6);
        g.beginPath(); g.moveTo(x1, fy);
        g.bezierCurveTo(x1 + len * 0.4, fy + ph * (rnd(`${t.n}:a${k}`) - 0.5) * 2, x1 + len * 0.7, fy + ph * (rnd(`${t.n}:b${k}`) - 0.5) * 3, x1 + len, fy + ph * (rnd(`${t.n}:c${k}`) - 0.5) * 4);
        g.stroke();
      }
    }
    // A thread ends in a small tie-off where it closed; a sudden event is a bright bead across the middle of its row.
    lanes.forEach((lane, j) => lane.forEach((sp) => {
      if (t.n !== sp.end || sp.end === Infinity) return;
      const c = ends.findIndex((e) => e.warp === j) + 1;
      const kx = pad + (c + 0.5) * cw, ky = pad + (i + 1) * picks * ph;
      g.fillStyle = P.knot; g.beginPath(); g.arc(kx, ky, Math.max(1.5, Math.min(cw * 0.7, picks * ph * 0.3)), 0, Math.PI * 2); g.fill();
    }));
    if (t.spike) {
      const kx = pad + iw * 0.5, ky = pad + (i + 0.5) * picks * ph;
      const kr = Math.max(3, Math.min(iw * 0.07, picks * ph * 0.7));
      g.fillStyle = P.knot; g.globalAlpha = 0.5; g.fillRect(pad, ky - Math.max(0.6, ph * 0.12), iw, Math.max(1.2, ph * 0.24)); g.globalAlpha = 1;
      const grad = g.createRadialGradient(kx - kr * 0.3, ky - kr * 0.3, kr * 0.1, kx, ky, kr);
      grad.addColorStop(0, "#fff"); grad.addColorStop(0.35, P.knot); grad.addColorStop(1, "rgba(0,0,0,.35)");
      g.fillStyle = grad; g.beginPath(); g.arc(kx, ky, kr, 0, Math.PI * 2); g.fill();
    }
  });
  // Fibre noise for the hand of the cloth.
  g.globalAlpha = 0.07; g.fillStyle = skin === "manuscript" || skin === "edition" ? "#3c2c1e" : "#ffffff";
  for (let k = 0; k < (W * H) / 700; k++) g.fillRect(rnd(`x${k}`) * W, rnd(`y${k}`) * H, 1, 1 + rnd(`l${k}`) * 3);
  g.globalAlpha = 1;
  if (view.ended) finishCloth(g, W, H, pad, P, (k) => rnd(`fringe${k}`));
}

/**
 * The story has ended: the cloth is finished, cast off. A tight selvedge woven along the bottom edge, and the warp
 * ends below it knotted into a fringe, bundle by bundle, a few in the threads' own colours.
 */
function finishCloth(g: CanvasRenderingContext2D, W: number, H: number, pad: number, P: (typeof PALETTES)[Skin], r: (k: number) => number): void {
  const [wr, wg, wb] = P.weft;
  const sh = Math.max(3, pad * 0.32), sy = H - pad - sh * 0.5;
  for (let x = pad, k = 0; x < W - pad; x += Math.max(2, sh * 0.5), k++) {
    g.fillStyle = `rgba(${wr},${wg},${wb},${k % 2 ? 0.85 : 0.55})`;
    g.fillRect(x, sy, Math.max(1, sh * 0.32), sh);
  }
  const bundles = Math.max(6, Math.round((W - pad * 2) / Math.max(10, pad * 1.1)));
  const step = (W - pad * 2) / bundles, top = sy + sh, len = Math.max(4, H - top - 1);
  for (let b = 0; b < bundles; b++) {
    const x = pad + (b + 0.5) * step;
    g.strokeStyle = b % 4 === 1 ? P.color(b) : `rgba(${wr},${wg},${wb},.75)`;
    g.lineWidth = Math.max(0.6, step * 0.06);
    for (let s = -2; s <= 2; s++) {
      g.beginPath(); g.moveTo(x + s * step * 0.08, top);
      g.quadraticCurveTo(x + s * step * 0.04, top + len * 0.4, x + s * step * 0.06 + (r(b * 5 + s) - 0.5) * step * 0.2, top + len * (0.75 + r(b * 7 + s) * 0.25));
      g.stroke();
    }
    g.fillStyle = P.knot; g.beginPath(); g.arc(x, top + len * 0.22, Math.max(1.2, step * 0.12), 0, Math.PI * 2); g.fill();
  }
}

/** A canvas sized for the device's pixel ratio. */
function useCanvas(view: StoryView, w: number, h: number) {
  const ref = useRef<HTMLCanvasElement>(null);
  useEffect(() => {
    const c = ref.current;
    if (!c) return;
    const dpr = Math.min(2, window.devicePixelRatio || 1);
    c.width = Math.round(w * dpr); c.height = Math.round(h * dpr);
    const g = c.getContext("2d")!;
    g.setTransform(dpr, 0, 0, dpr, 0, 0);
    drawCloth(g, view, w, h);
  }, [view, w, h]);
  return ref;
}

/** A small cloth for the morning screen: the story seen from above. */
export function ClothThumb({ view, w = 160, h = 200, className = "" }: { view: StoryView; w?: number; h?: number; className?: string }) {
  const ref = useCanvas(view, w, h);
  return <canvas ref={ref} className={`lm-cloth-thumb ${className}`} style={{ width: w, height: h }} aria-label="The story so far, as a woven cloth" />;
}

/** What the pointer is on: the turn (its row, the start of the player's line, tension, events) and the thread. */
function ClothCaption({ hover, onGo }: { hover: ReturnType<typeof hitTest>; onGo(n: number): void }) {
  if (!hover) return <p className="lm-cloth-caption lm-cloth-caption-empty">Hover over the cloth, or tap it, to see which turn and which thread you're on.</p>;
  const { row, thread, touched } = hover;
  const line = row.input.length > 70 ? `${row.input.slice(0, 68).trimEnd()}…` : row.input;
  const marks = [row.spike ? "a sudden event" : "", row.status === "cut" ? `cut (${row.unwoven ?? "some"} words unwoven)` : "", row.callback ? `${row.callback} came back` : ""].filter(Boolean);
  return (
    <div className="lm-cloth-caption" aria-live="polite">
      <p><strong>Row {row.n}</strong> · tension {(row.tension ?? 3).toFixed(1)}{marks.length ? ` · ${marks.join(" · ")}` : ""}</p>
      <p className="lm-cloth-line">you › {line}</p>
      {thread && <p>Thread: <strong>{thread.name}</strong>{touched ? ", touched in this turn" : ", running beneath this turn"}{thread.state === "closed" ? " (closed)" : ""}</p>}
      <button type="button" onClick={() => onGo(row.n)}>Go to row {row.n}</button>
    </div>
  );
}

export function Cloth({ view, onClose }: { view: StoryView; onClose(): void }) {
  const [size, setSize] = useState(() => { const w = Math.min(440, window.innerWidth - 68); return { w, h: Math.round(w * 1.25) }; });
  useEffect(() => {
    const on = () => { const w = Math.min(440, window.innerWidth - 68); setSize({ w, h: Math.round(w * 1.25) }); };
    window.addEventListener("resize", on);
    const key = (e: KeyboardEvent) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", key);
    return () => { window.removeEventListener("resize", on); window.removeEventListener("keydown", key); };
  }, [onClose]);
  const ref = useCanvas(view, size.w, size.h);
  const { rows } = layout(view);
  // Hover (mouse) or tap (touch) shows which turn and which thread are under the pointer; a mouse click, or the
  // "go to this turn" button on touch, goes there.
  const [hover, setHover] = useState<ReturnType<typeof hitTest>>(null);
  const at = (e: React.PointerEvent<HTMLCanvasElement>) => {
    const r = e.currentTarget.getBoundingClientRect();
    return hitTest(view, size.w, size.h, (e.clientX - r.left) * (size.w / r.width), (e.clientY - r.top) * (size.h / r.height));
  };
  const go = (n: number) => { onClose(); requestAnimationFrame(() => goToTurn(n)); };
  const tap = (e: React.PointerEvent<HTMLCanvasElement>) => {
    const h = at(e);
    if (!h) return;
    if (e.pointerType === "mouse") go(h.row.n);
    else setHover(h);
  };
  const pad = size.w * PAD;
  const band = hover ? { top: pad + (hover.index / hover.rows) * (size.h - pad * 2), height: (size.h - pad * 2) / hover.rows } : null;
  const save = () => {
    const c = document.createElement("canvas");
    c.width = 1080; c.height = 1350;
    const g = c.getContext("2d")!;
    drawCloth(g, view, 1080, 1350);
    // The cloth is computed, not drawn by a model, but it is woven from an AI-told story: said on the image and in
    // its metadata (EU AI Act transparency, owner's compliance list).
    g.save();
    g.font = "22px ui-monospace, Menlo, monospace"; g.textAlign = "center"; g.globalAlpha = 0.6;
    g.fillStyle = "#8a7f6c";
    g.fillText("weftwake · woven from a story told with an AI narrator", 540, 1350 - 1350 * PAD * 0.4);
    g.restore();
    c.toBlob((b) => {
      if (!b) return;
      void withText(b, {
        Title: "Weftwake cloth", Source: "Weftwake",
        "Digital Source Type": "http://cv.iptc.org/newscodes/digitalsourcetype/algorithmicMedia",
        Disclaimer: "Drawn from the shape of a story told with an AI narrator; contains no words from the story.",
      }).then((tagged) => {
        const a = document.createElement("a");
        a.href = URL.createObjectURL(tagged); a.download = `weftwake-cloth-${view.id.slice(0, 6)}.png`;
        document.body.append(a); a.click(); a.remove();
        setTimeout(() => URL.revokeObjectURL(a.href), 10_000);
      });
    }, "image/png");
  };
  const open = view.threads.filter((t) => t.state === "open").length;
  return (
    <div className="lm-cloth" role="dialog" aria-modal="true" aria-labelledby="lm-cloth-h" onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div className="lm-cloth-inner">
        <button type="button" className="lm-cloth-x" onClick={onClose} aria-label="Close the cloth">×</button>
        <h2 id="lm-cloth-h">The cloth</h2>
        <p className="lm-cloth-key"><strong>How to read it:</strong> your whole story as one woven cloth. Each row, top to bottom, is one of your turns; heavier, brighter rows were tenser. Each coloured stripe running down is a thread of the story, rising to the surface where a turn touched it. A bright bead across a row is a sudden event, a small knot is a thread tied off where it closed, bands are new chapters, and frayed rows are where you cut the narrator off.</p>
        <p className="lm-cloth-sub">{rows.length} rows · {view.threads.length} threads ({open} open) · hover or tap a row to see its turn</p>
        <div className="lm-cloth-frame" style={{ width: size.w, height: size.h }}>
          <canvas ref={ref} style={{ width: size.w, height: size.h }}
            onPointerMove={(e) => { if (e.pointerType === "mouse") setHover(at(e)); }}
            onPointerLeave={(e) => { if (e.pointerType === "mouse") setHover(null); }}
            onPointerUp={tap}
            aria-label={`The story as a woven cloth: ${rows.length} rows, ${view.threads.length} threads. Hover or tap a row to see its turn.`} />
          {band && <div className="lm-cloth-row" style={{ top: band.top, height: band.height }} aria-hidden="true" />}
        </div>
        <ClothCaption hover={hover} onGo={go} />
        <div className="lm-cloth-actions">
          <button type="button" onClick={save}>Save the cloth</button>
          <button type="button" className="lm-cta" onClick={onClose}>Back to the story</button>
        </div>
      </div>
    </div>
  );
}
